import React, { useState, useEffect } from 'react';
import { TrendingUp, TrendingDown, Zap, Crown } from 'lucide-react';

interface CreditChangeAnimationProps {
  oldValue: number | 'unlimited';
  newValue: number | 'unlimited';
  type: 'credits' | 'scans';
  show: boolean;
  onComplete: () => void;
  className?: string;
}

export const CreditChangeAnimation: React.FC<CreditChangeAnimationProps> = ({
  oldValue,
  newValue,
  type,
  show,
  onComplete,
  className = ''
}) => {
  const [stage, setStage] = useState<'enter' | 'highlight' | 'exit'>('enter');
  const [currentValue, setCurrentValue] = useState(oldValue);

  useEffect(() => {
    if (!show) return;

    const sequence = async () => {
      // Stage 1: Enter animation
      setStage('enter');
      
      // Stage 2: Highlight the change
      setTimeout(() => {
        setCurrentValue(newValue);
        setStage('highlight');
      }, 200);
      
      // Stage 3: Exit animation
      setTimeout(() => {
        setStage('exit');
      }, 1500);
      
      // Complete
      setTimeout(() => {
        onComplete();
      }, 2000);
    };

    sequence();
  }, [show, newValue, onComplete]);

  if (!show) return null;

  const isIncrease = typeof oldValue === 'number' && typeof newValue === 'number' 
    ? newValue > oldValue 
    : newValue === 'unlimited';
  
  const isUnlimited = newValue === 'unlimited';
  const change = typeof oldValue === 'number' && typeof newValue === 'number' 
    ? Math.abs(newValue - oldValue) 
    : 0;

  const getIcon = () => {
    if (isUnlimited) return Crown;
    return isIncrease ? TrendingUp : TrendingDown;
  };

  const getColor = () => {
    if (isUnlimited) return 'text-yellow-400';
    return isIncrease ? 'text-green-400' : 'text-red-400';
  };

  const getBgColor = () => {
    if (isUnlimited) return 'bg-yellow-900/30 border-yellow-700/50';
    return isIncrease ? 'bg-green-900/30 border-green-700/50' : 'bg-red-900/30 border-red-700/50';
  };

  const Icon = getIcon();

  return (
    <div className={`fixed top-20 right-4 z-50 ${className}`}>
      <div 
        className={`
          transform transition-all duration-300 ease-in-out
          ${stage === 'enter' ? 'translate-x-full opacity-0' : ''}
          ${stage === 'highlight' ? 'translate-x-0 opacity-100 scale-105' : ''}
          ${stage === 'exit' ? 'translate-x-0 opacity-0 scale-95' : ''}
        `}
      >
        <div className={`
          p-4 rounded-lg border backdrop-blur-sm shadow-lg
          ${getBgColor()}
        `}>
          <div className="flex items-center gap-3">
            <div className={`
              p-2 rounded-full bg-gray-800 border
              ${isUnlimited ? 'border-yellow-700/50' : isIncrease ? 'border-green-700/50' : 'border-red-700/50'}
            `}>
              <Icon className={`w-5 h-5 ${getColor()}`} />
            </div>
            
            <div className="flex-1">
              <div className="text-white font-medium">
                {isUnlimited ? 'Unlimited Credits!' : 
                 isIncrease ? `+${change} ${type}` : 
                 `-${change} ${type}`}
              </div>
              <div className="text-sm text-gray-400">
                {isUnlimited ? 'Pro subscription activated' :
                 `${oldValue} → ${newValue} ${type}`}
              </div>
            </div>
            
            {/* Sparkle effect for unlimited */}
            {isUnlimited && (
              <div className="relative">
                <Zap className="w-6 h-6 text-yellow-400 animate-pulse" />
                <div className="absolute -top-1 -right-1 w-3 h-3 bg-yellow-400 rounded-full animate-ping opacity-30"></div>
              </div>
            )}
          </div>
          
          {/* Progress bar for animation */}
          <div className="mt-3 w-full bg-gray-700 rounded-full h-1">
            <div 
              className={`
                h-1 rounded-full transition-all duration-1500 ease-out
                ${isUnlimited ? 'bg-yellow-400' : isIncrease ? 'bg-green-400' : 'bg-red-400'}
                ${stage === 'highlight' ? 'w-full' : 'w-0'}
              `}
            ></div>
          </div>
        </div>
      </div>
    </div>
  );
}; 